import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { Circles } from "react-loader-spinner";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Country } from "country-state-city";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSun, faMoon, faMapMarkerAlt, faCalendarAlt, faClock,
  faUsers, faShareAlt, faFilter, faArrowRight, faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { IoShareOutline } from "react-icons/io5";
import logo from "../assets/logo.png";

/* ── Theme tokens (mirrors LaunchPage) ── */
const DARK = {
  mode: "dark",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.08)",
  accentBgLight: "rgba(12,105,200,0.12)",
  accentBorder: "rgba(12,105,200,0.20)",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.35)",
  bg: "#0A0A0A",
  bgCard: "#141414",
  bgAlt: "#111111",
  bgElevated: "#1E1E1E",
  border: "rgba(255,255,255,0.08)",
  borderMedium: "rgba(255,255,255,0.12)",
  shadowMd: "0 4px 16px rgba(0,0,0,0.4)",
  shadowLg: "0 12px 40px rgba(0,0,0,0.5)",
  text: "#FAFAFA",
  textSecondary: "rgba(255,255,255,0.70)",
  textMuted: "rgba(255,255,255,0.40)",
  headerBg: "rgba(10,10,10,0.92)",
  scrollbarThumb: "rgba(255,255,255,0.12)",
  tagBg: "rgba(255,255,255,0.05)",
  tagBorder: "rgba(255,255,255,0.10)",
  tagText: "rgba(255,255,255,0.60)",
  success: "#34D399",
  danger: "#EF4444",
};

const LIGHT = {
  mode: "light",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.05)",
  accentBgLight: "rgba(12,105,200,0.08)",
  accentBorder: "rgba(12,105,200,0.15)",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.25)",
  bg: "#F5F7FA",
  bgCard: "#FFFFFF",
  bgAlt: "#EDF0F5",
  bgElevated: "#FFFFFF",
  border: "rgba(10,29,68,0.08)",
  borderMedium: "rgba(10,29,68,0.12)",
  shadowMd: "0 4px 16px rgba(10,29,68,0.08)",
  shadowLg: "0 12px 40px rgba(10,29,68,0.12)",
  text: "#0F1629",
  textSecondary: "#3D4A6B",
  textMuted: "#7A87A8",
  headerBg: "rgba(245,247,250,0.95)",
  scrollbarThumb: "rgba(10,29,68,0.12)",
  tagBg: "rgba(12,105,200,0.06)",
  tagBorder: "rgba(12,105,200,0.12)",
  tagText: "#0C69C8",
  success: "#059669",
  danger: "#DC2626",
};

const validCountries = Country.getAllCountries()
  .map((country) => country.name)
  .sort((a, b) => a.localeCompare(b));

const toUrlSegment = (str) => str ? str.replace(/\s+/g, "").toLowerCase() : "";

const Sessions = () => {
  const navigate = useNavigate();
  const { param1, param2, param3 } = useParams();

  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("spa-theme") !== "light"; } catch { return true; }
  });
  const C = isDark ? DARK : LIGHT;

  const toggleTheme = useCallback(() => {
    setIsDark((v) => {
      const next = !v;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);

  const [selectedSport, setSelectedSport] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [userCoords, setUserCoords] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validSports, setValidSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(true);
  const [costType, setCostType] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchValidSports = async () => {
      const { data, error } = await supabase
        .from("sports")
        .select("id")
        .eq("active", true)
        .order("id", { ascending: true });

      if (mounted) {
        setValidSports(error ? [] : (data || []).map((sport) => sport.id).filter(Boolean));
        setSportsLoading(false);
      }
    };

    fetchValidSports();

    return () => {
      mounted = false;
    };
  }, []);

  /* ── Parse URL params ── */
  useEffect(() => {
    if (sportsLoading) return;

    const allParams = [param1, param2, param3].filter(Boolean);
    let sport = "", country = "", cost = "";
    allParams.forEach((param) => {
      const normalized = toUrlSegment(param);
      if (validSports.some((s) => toUrlSegment(s) === normalized))
        sport = validSports.find((s) => toUrlSegment(s) === normalized);
      else if (validCountries.some((c) => toUrlSegment(c) === normalized))
        country = validCountries.find((c) => toUrlSegment(c) === normalized);
      else if (["free","paid"].includes(normalized))
        cost = normalized;
    });
    if (allParams.length !== [sport, country, cost].filter(Boolean).length) {
      navigate("/sessions", { replace: true }); return;
    }
    setSelectedSport(sport);
    setSelectedCountry(country);
    setCostType(cost);
  }, [param1, param2, param3, sportsLoading, validSports, navigate]);

  /* ── Fetch sessions ── */
  const fetchSessions = useCallback(async () => {
    const { data, error } = await supabase
      .from("scheduled_sessions")
      .select(`*, locations (name, address, image_url, country, latitude, longitude)`)
      .is("institute_id", null)
      .order("date", { ascending: true });
    if (error) { setLoading(false); return; }
    const updated = (data || []).map((session) => {
      const hasLocation = !!session.locations;
      const source = hasLocation ? session.locations : session.custom_location;
      return {
        ...session,
        location: source?.name || "Unknown Location",
        address: source?.address || "No Address Available",
        imageUrl: source?.image_url || "https://example.com/default-image.png",
        country: hasLocation ? session.locations?.country || "Unknown" : session.country || "Unknown",
      };
    });
    const today = new Date(); today.setHours(0,0,0,0);
    const final = updated.filter((s) => {
      const d = new Date(s.date); d.setHours(0,0,0,0);
      return d >= today && !s.institute_id;
    });
    setSessions(final);
    setLoading(false);
  }, []);

  useEffect(() => { fetchSessions(); }, [selectedCountry, selectedSport]);

  useEffect(() => {
    const getLocation = async () => {
      try {
        const position = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true })
        );
        setUserCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
      } catch { /* no-op */ }
      fetchSessions();
    };
    getLocation();
  }, []);

  /* ── Filtered view ── */
  const filteredSessions = sessions.filter((session) => {
    const matchesSport = !selectedSport || session.sport_id === selectedSport;
    const matchesCountry = !selectedCountry || session.country === selectedCountry;
    const matchesCost = costType === "free" ? session.free === true : costType === "paid" ? session.free === false : true;
    return matchesSport && matchesCountry && matchesCost;
  });

  /* ── URL builder ── */
  const buildPath = (country, sport, cost) => {
    const segs = [
      country && toUrlSegment(country),
      sport && toUrlSegment(sport),
      cost && cost.toLowerCase(),
    ].filter(Boolean);
    return segs.length ? `/sessions/${segs.join("/")}` : "/sessions";
  };

  /* ── Share handler ── */
  const handleShare = (session, locationName) => {
    const sessionDateFormatted = new Date(session.date).toLocaleDateString();
    const startTime = session.start_time
      ? (() => {
          const [h, m] = session.start_time.split(":");
          const d = new Date(); d.setHours(parseInt(h), parseInt(m));
          return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        })()
      : "TBD";
    const isFull = session.spots_left === 0;
    const spots = session.spots_left != null && session.player_limit != null
      ? `${session.spots_left} spot(s) left` : "Spots unknown";
    const type = session.free ? "Free" : "Paid";
    const info = session.session_info ? `\n\n*📝 Session Info:*\n${session.session_info}` : "";
    const shareText = `*${session.sport_id} session!* 🔥\n\n📍 *Location:* ${locationName}\n📅 *Date, Time:* ${sessionDateFormatted}, ${startTime}\n🎟️ *Type:* ${type}\n👥 *Availability:* ${isFull ? "Full 🔒" : spots}${info}\n\nJoin via: https://linktr.ee/snowsproam`;
    if (navigator.share) {
      navigator.share({ title: "Snows ProAm Session", text: shareText }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
    }
  };

  /* ── Select style helper ── */
  const selectStyle = () => ({
    padding: "10px 36px 10px 14px", borderRadius: 10, cursor: "pointer",
    border: `1px solid ${C.border}`,
    backgroundColor: C.bgCard, color: C.text,
    fontSize: 13.5, fontWeight: 500, fontFamily: "'DM Sans', sans-serif",
    outline: "none", appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${encodeURIComponent(isDark ? "rgba(255,255,255,0.4)" : "rgba(10,29,68,0.35)")}' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", backgroundSize: "12px",
    boxShadow: C.shadowMd, transition: "border-color 0.15s ease, background-color 0.2s ease",
    width: "100%",
  });

  if (loading || sportsLoading) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Circles height="60" width="60" color={C.accent} ariaLabel="loading" />
      </div>
    );
  }

  return (
    <div style={{
      background: C.bg, minHeight: "100vh", color: C.text,
      fontFamily: "'DM Sans', sans-serif",
      transition: "background 0.3s ease, color 0.3s ease",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; -webkit-font-smoothing: antialiased; }
        a { color: inherit; text-decoration: none; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.scrollbarThumb}; border-radius: 99px; }
        .theme-toggle { transition: transform 0.2s ease; }
        .theme-toggle:hover { transform: rotate(15deg) scale(1.1); }
        .grad-text {
          background: linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .session-card {
          transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.25s ease;
        }
        .session-card:hover {
          border-color: ${C.accentBorder} !important;
          box-shadow: ${C.shadowLg} !important;
          transform: translateY(-2px);
        }
        .join-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          background: linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%);
          color: #fff; border: none; border-radius: 10px;
          padding: 12px; font-size: 14px; font-weight: 700;
          font-family: 'Sora', sans-serif; cursor: pointer; text-decoration: none;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          width: 100%;
        }
        .join-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px ${C.accentGlow}; }
        select option { background: ${isDark ? "#141414" : "#ffffff"}; color: ${isDark ? "#FAFAFA" : "#0F1629"}; }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp 0.5s ease forwards; }
        .fade-up:nth-child(odd) { animation-delay: 0.05s; }
        .fade-up:nth-child(even) { animation-delay: 0.1s; }

        /* ── Mobile filter layout ── */
        .filter-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          align-items: flex-end;
          justify-content: center;
          width: 100%;
        }
        .filter-item {
          display: flex;
          flex-direction: column;
          flex: 1 1 140px;
          min-width: 140px;
        }
        @media (max-width: 480px) {
          .filter-item {
            flex: 1 1 100%;
          }
          .filter-label-row {
            justify-content: center;
          }
          .site-header-center {
            display: none !important;
          }
        }
      `}</style>

      {/* ── Header ── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100, height: 56, padding: "0 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: C.headerBg,
        backdropFilter: "blur(20px) saturate(1.2)",
        WebkitBackdropFilter: "blur(20px) saturate(1.2)",
        borderBottom: `1px solid ${C.border}`,
        transition: "background 0.3s ease",
      }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <img src={logo} alt="Snows ProAm" style={{ width: 18, height: 18, objectFit: "contain" }} />
          </div>
          <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 14, fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>
            ProAm <span className="grad-text">Sessions</span>
          </span>
        </Link>
        <div className="site-header-center" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, fontSize: 10, fontWeight: 800, color: C.accent, letterSpacing: 0.4, textTransform: "uppercase", pointerEvents: "none" }}>
          Live Sessions
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="theme-toggle" aria-label={isDark ? "Use light theme" : "Use dark theme"} onClick={toggleTheme} style={{
            width: 32, height: 32, borderRadius: 8,
            background: C.bgElevated, border: `1px solid ${C.border}`,
            color: C.text, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          }}>
            <FontAwesomeIcon icon={isDark ? faSun : faMoon} style={{ fontSize: 12 }} />
          </button>
          <button type="button" onClick={() => navigate(-1)} style={{ fontSize: 12, fontWeight: 500, color: C.textSecondary, padding: "5px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgElevated, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
            ← Back
          </button>
        </div>
      </header>

      {/* ── Hero / Title ── */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px 32px" }}>
        <h1 style={{
          fontFamily: "'Sora', sans-serif",
          fontSize: "clamp(32px, 5vw, 52px)",
          fontWeight: 800, letterSpacing: -1.2, lineHeight: 1.05,
          marginBottom: 12, color: C.text,
        }}>
          Find your next <span className="grad-text">session</span>
        </h1>
        <p style={{ fontSize: 16, color: C.textSecondary, lineHeight: 1.6, maxWidth: 480, marginBottom: 32 }}>
          Browse upcoming sessions across sports and countries. Join in one tap.
        </p>

        {/* ── Filters ── */}
        <div style={{
          background: C.bgCard, border: `1px solid ${C.border}`,
          borderRadius: 16, padding: "20px 24px",
          boxShadow: C.shadowMd,
        }}>
          {/* Filter label row */}
          <div className="filter-label-row" style={{
            display: "flex", alignItems: "center", gap: 8,
            justifyContent: "center", marginBottom: 16,
          }}>
            <FontAwesomeIcon icon={faFilter} style={{ fontSize: 13, color: C.accent }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Filter
            </span>
            {(selectedCountry || selectedSport || costType) && (
              <div style={{
                padding: "4px 10px", borderRadius: 999,
                background: C.accentBgLight, border: `1px solid ${C.accentBorder}`,
                fontSize: 12, fontWeight: 700, color: C.accent,
              }}>
                {[selectedCountry, selectedSport, costType].filter(Boolean).length} active
              </div>
            )}
          </div>

          {/* Filter inputs row */}
          <div className="filter-row">
            {/* Country */}
            <div className="filter-item">
              <label style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.4, display: "block", marginBottom: 6, textAlign: "center" }}>Country</label>
              <select aria-label="Country" value={selectedCountry} style={selectStyle()}
                onChange={(e) => {
                  const c = e.target.value; setSelectedCountry(c);
                  navigate(buildPath(c, selectedSport, costType));
                }}>
                <option value="">All Countries</option>
                {validCountries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Sport */}
            <div className="filter-item">
              <label style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.4, display: "block", marginBottom: 6, textAlign: "center" }}>Sport</label>
              <select aria-label="Sport" value={selectedSport} style={selectStyle()}
                onChange={(e) => {
                  const s = e.target.value; setSelectedSport(s);
                  navigate(buildPath(selectedCountry, s, costType));
                }}>
                <option value="">All Sports</option>
                {validSports.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Cost */}
            <div className="filter-item">
              <label style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.4, display: "block", marginBottom: 6, textAlign: "center" }}>Cost</label>
              <select aria-label="Cost" value={costType} style={selectStyle()}
                onChange={(e) => {
                  const cost = e.target.value; setCostType(cost);
                  navigate(buildPath(selectedCountry, selectedSport, cost));
                }}>
                <option value="">All</option>
                <option value="free">Free</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sessions grid ── */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px 80px" }}>
        {filteredSessions.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "72px 24px",
            background: C.bgCard, border: `1px solid ${C.border}`,
            borderRadius: 20, boxShadow: C.shadowMd,
          }}>
            <div style={{
              fontSize: 48, marginBottom: 16, opacity: 0.4,
            }}>🏟️</div>
            <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 10 }}>
              No sessions available right now
            </h3>
            <p style={{ fontSize: 14, color: C.textSecondary, marginBottom: 20, lineHeight: 1.6 }}>
              Want to host one? Download the app to create a session.
            </p>
            <a
              href="https://linktr.ee/snowsproam"
              target="_blank" rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
                color: "#fff", padding: "12px 24px", borderRadius: 10,
                fontSize: 14, fontWeight: 700, fontFamily: "'Sora', sans-serif",
              }}>
              Download the app <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
            </a>
          </div>
        ) : (
          <>
            {/* Results count */}
            <div style={{ marginBottom: 20, fontSize: 13, color: C.textMuted, fontWeight: 500 }}>
              <span style={{ color: C.accent, fontWeight: 700 }}>{filteredSessions.length}</span>{" "}
              session{filteredSessions.length !== 1 ? "s" : ""} found
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(280px, 100%), 1fr))",
              gap: 16,
            }}>
              {filteredSessions.map((session) => {
                const isCustom = !!session.custom_location;
                const locationSource = isCustom ? session.custom_location : session.locations;
                const locationName = locationSource?.name || "Unknown Location";
                const locationAddress = locationSource?.address || "No Address Available";
                const imageUrl = locationSource?.imageUrl || locationSource?.image_url || "https://example.com/default-image.png";

                const today = new Date(); today.setHours(0,0,0,0);
                const sessionDate = new Date(session.date); sessionDate.setHours(0,0,0,0);
                const diffDays = Math.ceil((sessionDate - today) / (1000 * 60 * 60 * 24));

                const startTimeFormatted = session.start_time
                  ? (() => {
                      const [h, m] = session.start_time.split(":");
                      const d = new Date(); d.setHours(parseInt(h), parseInt(m));
                      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
                    })()
                  : "TBD";

                const isFull = session.spots_left === 0;

                return (
                  <div key={session.id} className="session-card fade-up" style={{
                    background: C.bgCard,
                    border: `1px solid ${C.border}`,
                    borderRadius: 16, overflow: "hidden",
                    boxShadow: C.shadowMd,
                    display: "flex", flexDirection: "column",
                  }}>
                    {/* Image */}
                    <div style={{ position: "relative" }}>
                      <img src={imageUrl} alt={locationName} style={{
                        width: "100%", height: 160, objectFit: "cover", display: "block",
                      }} />
                      {/* Sport badge overlay */}
                      <div style={{
                        position: "absolute", bottom: 10, left: 12,
                        padding: "4px 10px", borderRadius: 999,
                        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
                        fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: 0.3,
                      }}>{session.sport_id}</div>
                      {/* Free/paid badge */}
                      <div style={{
                        position: "absolute", top: 10, right: 10,
                        padding: "4px 10px", borderRadius: 999,
                        background: session.free
                          ? "rgba(52,211,153,0.20)" : "rgba(12,105,200,0.25)",
                        backdropFilter: "blur(6px)",
                        border: `1px solid ${session.free ? "rgba(52,211,153,0.35)" : "rgba(12,105,200,0.35)"}`,
                        fontSize: 11, fontWeight: 700,
                        color: session.free ? "#34D399" : "#5BA8F7",
                      }}>{session.free ? "Free" : "Paid"}</div>
                      {/* Urgency badge */}
                      {diffDays <= 1 && (
                        <div style={{
                          position: "absolute", top: 10, left: 12,
                          padding: "4px 10px", borderRadius: 999,
                          background: "rgba(239,68,68,0.20)", backdropFilter: "blur(6px)",
                          border: "1px solid rgba(239,68,68,0.35)",
                          fontSize: 11, fontWeight: 700, color: "#FCA5A5",
                        }}>{diffDays === 0 ? "Today!" : "Tomorrow!"}</div>
                      )}
                      {/* Share button */}
                      <button
                        onClick={() => handleShare(session, locationName)}
                        style={{
                          position: "absolute", bottom: 10, right: 12,
                          width: 32, height: 32, borderRadius: 8,
                          background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
                          border: "1px solid rgba(255,255,255,0.15)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", color: "#fff",
                        }}
                        title="Share session">
                        <IoShareOutline style={{ fontSize: 15 }} />
                      </button>
                    </div>

                    {/* Content */}
                    <div style={{ padding: "18px 18px 20px", display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
                      <h3 style={{
                        fontFamily: "'Sora', sans-serif", fontSize: 15, fontWeight: 700,
                        color: C.text, lineHeight: 1.3,
                      }}>{locationName}</h3>

                      <div style={{ display: "grid", gap: 6 }}>
                        {/* Address */}
                        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                          <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: 12, color: C.accent, marginTop: 2, flexShrink: 0 }} />
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationAddress)}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.4, textDecoration: "underline", textDecorationColor: "transparent" }}
                            onMouseEnter={(e) => e.currentTarget.style.textDecorationColor = C.accent}
                            onMouseLeave={(e) => e.currentTarget.style.textDecorationColor = "transparent"}>
                            {locationAddress}
                          </a>
                        </div>

                        {/* Date & time row */}
                        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: 12, color: C.accent }} />
                            <span style={{ fontSize: 13, color: C.textSecondary }}>
                              {session.date ? new Date(session.date).toLocaleDateString() : "TBD"}
                            </span>
                          </div>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <FontAwesomeIcon icon={faClock} style={{ fontSize: 12, color: C.accent }} />
                            <span style={{ fontSize: 13, color: C.textSecondary }}>{startTimeFormatted}</span>
                          </div>
                        </div>

                        {/* Spots */}
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <FontAwesomeIcon icon={faUsers} style={{ fontSize: 12, color: isFull ? C.danger : C.success }} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: isFull ? C.danger : C.textSecondary }}>
                            {isFull
                              ? "Full"
                              : session.spots_left != null && session.player_limit != null
                              ? `${session.spots_left} spot${session.spots_left !== 1 ? "s" : ""} left`
                              : "Open"}
                          </span>
                        </div>
                      </div>

                      {/* Session info */}
                      {session.session_info && (
                        <p style={{
                          fontSize: 13, color: C.textMuted, fontStyle: "italic",
                          lineHeight: 1.55, padding: "10px 12px",
                          background: C.bgAlt, borderRadius: 8,
                          border: `1px solid ${C.border}`,
                        }}>{session.session_info}</p>
                      )}

                      {/* Countdown chip (> 1 day) */}
                      {diffDays > 1 && diffDays <= 10 && (
                        <div style={{
                          padding: "5px 10px", borderRadius: 999,
                          background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
                          fontSize: 12, fontWeight: 600, color: C.accent,
                          display: "inline-flex", alignSelf: "flex-start",
                        }}>
                          {diffDays} days away
                        </div>
                      )}

                      {/* Join CTA — pushed to bottom */}
                      <div style={{ marginTop: "auto", paddingTop: 8 }}>
                        <a href="https://linktr.ee/snowsproam" target="_blank" rel="noopener noreferrer" className="join-btn">
                          Join Session <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Sessions;
